'use client'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useId, useState } from 'react'
import type { ArtistQaContent } from '../artistQa'

interface ArtistQaSectionProps {
  content: ArtistQaContent
}

export const ArtistQaSection = ({ content }: ArtistQaSectionProps) => {
  const baseId = useId()
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section className="border-b border-gray-200 bg-gray-50">
      <div className="mx-auto grid w-full gap-8 px-4 py-12 sm:px-6 lg:max-w-screen-lg lg:grid-cols-[0.38fr_1fr] lg:py-16 xl:max-w-screen-xl xl:px-0">
        <div>
          <p className="text-sm font-bold uppercase text-gray-700">
            {content.eyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-black leading-tight text-gray-950 sm:text-4xl">
            {content.title}
          </h2>
        </div>

        <div className="border-t border-gray-300/80">
          {content.items.map((item, index) => {
            const triggerId = `${baseId}-qa-trigger-${index}`
            const panelId = `${baseId}-qa-panel-${index}`
            const isOpen = openIndex === index

            return (
              <div key={item.question} className="border-b border-gray-300/80">
                <h3 className="m-0 text-xl font-bold">
                  <button
                    id={triggerId}
                    type="button"
                    onClick={() =>
                      setOpenIndex(prev => (prev === index ? null : index))
                    }
                    className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-left text-xl font-black text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span>{item.question}</span>
                    <ChevronDownIcon
                      className={`size-6 shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  hidden={!isOpen}
                  className="pb-6 text-lg leading-8 text-gray-700 [&_a]:font-medium [&_a]:text-vi-red [&_a:hover]:text-red-800"
                  dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
